import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Keeps a page that throws while rendering from blanking the whole app.
 *
 * Without it React unmounts everything, the navigation included, and the person
 * sees an empty screen with the reason only in the console. That is how the
 * Model Gateway page failed when the router's discover answer was a bare number
 * (2026-10-21). This shows the error inside the shell instead, so they can say
 * what happened and still move to another page.
 *
 * It is a backstop, not the fix: a page should check what it is given before it
 * renders it, as the Add Models list now does (pages/discover-answer.ts).
 */
export class PageErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[page] stopped rendering:', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="mx-auto max-w-xl p-8">
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 px-5 py-4">
          <p className="text-sm font-medium text-red-300 mb-1">This page stopped working</p>
          <p className="text-sm text-red-300/70 mb-3 break-words">{error.message || String(error)}</p>
          <button onClick={() => window.location.reload()} className="text-xs text-white/50 hover:text-white/70">
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}
